/* Selector file rule: root-level functions in this file must be exported selectors only. Move helper functions to lib files. */
import { createSelector } from 'reselect';
import getModelReasoningOptions from '@/lib/model/getModelReasoningOptions.js';
import { NON_RUNNING_RUN_STATES } from '@/lib/pieveRunState.js';
import { threadContentMap, threadMetaById } from '@/redux/selectors/pieveSelectors/baseSelectors.js';
import { automationTriggerItems } from '@/redux/selectors/pieveSelectors/automationSharedSelectors.js';
import getAutomationComposerModelId from '@/redux/selectors/pieveSelectors/lib/getAutomationComposerModelId.js';
import getAutomationComposerReasoningEffort from '@/redux/selectors/pieveSelectors/lib/getAutomationComposerReasoningEffort.js';
import getAutomationItemThreadId from '@/redux/selectors/pieveSelectors/lib/getAutomationItemThreadId.js';
import getAutomationRouteRequestedId from '@/redux/selectors/pieveSelectors/lib/getAutomationRouteRequestedId.js';
import getCurrentAutomationRequestedId from '@/redux/selectors/pieveSelectors/lib/getCurrentAutomationRequestedId.js';
import getCurrentAutomationRunThreadId from '@/redux/selectors/pieveSelectors/lib/getCurrentAutomationRunThreadId.js';
import getNormalizedThreadId from '@/redux/selectors/pieveSelectors/lib/getNormalizedThreadId.js';
import getThreadContentValue from '@/redux/selectors/pieveSelectors/lib/getThreadContentValue.js';
import getThreadMetaValue from '@/redux/selectors/pieveSelectors/lib/getThreadMetaValue.js';
import getThreadParentId from '@/redux/selectors/pieveSelectors/lib/getThreadParentId.js';
import getThreadType from '@/redux/selectors/pieveSelectors/lib/getThreadType.js';
import getTriggerPollBlockedMessage from '@/redux/selectors/pieveSelectors/lib/getTriggerPollBlockedMessage.js';
import { appsMetadataItems } from '@/redux/selectors/pieveSelectors/runtimeSelectors.js';
import { activeThreadId } from '@/redux/selectors/pieveSelectors/activeThreadIdSelector.js';
import { threadActiveRunId, threadRunState } from '@/redux/selectors/pieveSelectors/threadRunSelectors.js';
import { automationTriggerRunSelectionContext as selectedAutomationTriggerRunSelectionContext, automationTriggerSelectionId as selectedAutomationTriggerSelectionId, pathname as selectedPathname, query as selectedQuery } from '@/redux/selectors/uiSelectors.js';
import { findTriggerByRequestedId } from '@/lib/automationTriggers/triggerState.js';

export const currentAutomationTriggerRequestedId = createSelector([selectedPathname, activeThreadId, threadMetaById, selectedAutomationTriggerSelectionId], (pathname, activeThreadId, threadMetaByIdValue, selectedTriggerId) => {
  return getCurrentAutomationRequestedId({
    routeRequestedId: getAutomationRouteRequestedId({
      pathname,
      automationType: 'triggers'
    }),
    activeThreadId,
    threadMetaByIdValue,
    ownerThreadType: 'trigger',
    runThreadType: 'trigger_run',
    selectedRequestedId: selectedTriggerId
  });
});

export const currentAutomationTrigger = createSelector([automationTriggerItems, currentAutomationTriggerRequestedId], (triggerItems, requestedId) => {
  if (!requestedId) {
    return null;
  }

  return findTriggerByRequestedId({
    triggers: triggerItems,
    requestedId
  });
});

export const currentAutomationTriggerId = createSelector([currentAutomationTrigger], (trigger) => {
  return String(trigger?.id || '').trim() || null;
});

export const currentAutomationTriggerPollBlockedMessage = createSelector([currentAutomationTrigger], (trigger) => {
  return getTriggerPollBlockedMessage({ trigger });
});

export const currentAutomationTriggerConfig = createSelector([currentAutomationTrigger], (trigger) => {
  if (!trigger) {
    return null;
  }

  const triggerConfig = trigger.triggerConfig && typeof trigger.triggerConfig === 'object' ? trigger.triggerConfig : {};

  return {
    id: trigger.id || null,
    title: trigger.title || 'Untitled trigger',
    prompt: trigger.prompt || '',
    active: Boolean(trigger.active),
    triggerType: trigger.triggerType || triggerConfig.triggerType || 'app_event',
    triggerSummary: trigger.triggerSummary || 'No trigger configured',
    appId: trigger.appId || triggerConfig.appId || null,
    actionId: trigger.actionId || triggerConfig.actionId || null,
    externalCompanyId: trigger.externalCompanyId || triggerConfig.externalCompanyId || null,
    credentialId: trigger.credentialId || triggerConfig.credentialId || null,
    pollIntervalMinutes: trigger.pollIntervalMinutes || triggerConfig.pollIntervalMinutes || null,
    promptSummary: trigger.promptSummary || null,
    actionLabel: trigger.actionLabel || null,
    composerModel: trigger.composerModel || trigger.details?.composerModel || null,
    composerReasoningEffort: trigger.composerReasoningEffort || trigger.details?.composerReasoningEffort || null,
    threadId: trigger.threadId || null,
    triggerConfig: {
      ...triggerConfig,
      triggerType: trigger.triggerType || triggerConfig.triggerType || 'app_event'
    }
  };
});

export const currentAutomationTriggerAppIconUrl = createSelector([currentAutomationTriggerConfig, appsMetadataItems], (triggerConfig, apps) => {
  const appId = String(triggerConfig?.appId || '').trim();

  if (!appId) {
    return '';
  }

  const appEntry = (Array.isArray(apps) ? apps : []).find((app) => app?.id === appId);

  return String(appEntry?.icon || '').trim();
});

export const currentAutomationTriggerComposerModel = createSelector([currentAutomationTrigger, threadMetaById], (trigger, threadMetaByIdValue) => {
  return getAutomationComposerModelId({
    item: trigger,
    threadMetaByIdValue
  });
});

export const currentAutomationTriggerComposerReasoningEffort = createSelector([currentAutomationTrigger, threadMetaById, currentAutomationTriggerComposerModel], (trigger, threadMetaByIdValue, modelId) => {
  return getAutomationComposerReasoningEffort({
    item: trigger,
    threadMetaByIdValue,
    modelId
  });
});

export const currentAutomationTriggerRunThreadId = createSelector([selectedPathname, (state) => selectedQuery(state, 'run'), currentAutomationTriggerId, selectedAutomationTriggerRunSelectionContext], (pathname, routeRunThreadId, currentTriggerId, selectionContext) => {
  return getCurrentAutomationRunThreadId({
    pathname,
    routeRunThreadId,
    currentOwnerId: currentTriggerId,
    selectionContext,
    automationType: 'triggers',
    selectionOwnerKey: 'triggerId'
  });
});

export const currentAutomationTriggerComposerReasoningOptions = createSelector([currentAutomationTriggerComposerModel], (modelId) => {
  return getModelReasoningOptions(modelId);
});

export const currentAutomationTriggerSelectedRunThreadId = createSelector([currentAutomationTriggerRunThreadId, currentAutomationTrigger, threadMetaById], (selectedRunThreadId, trigger, threadMetaByIdValue) => {
  const normalizedRunThreadId = getNormalizedThreadId(selectedRunThreadId);

  if (!normalizedRunThreadId) {
    return null;
  }

  const runThreadMeta = threadMetaByIdValue?.[normalizedRunThreadId] || null;

  if (getThreadType(runThreadMeta) !== 'trigger_run') {
    return null;
  }

  const triggerThreadId = getAutomationItemThreadId({ item: trigger });
  const parentThreadId = getThreadParentId(runThreadMeta);

  if (triggerThreadId && parentThreadId && triggerThreadId !== parentThreadId) {
    return null;
  }

  return normalizedRunThreadId;
});

export const currentAutomationTriggerThreadId = createSelector([currentAutomationTriggerSelectedRunThreadId, currentAutomationTrigger], (runThreadId, trigger) => {
  return runThreadId || getAutomationItemThreadId({ item: trigger });
});

export const currentAutomationTriggerThreadContent = createSelector([threadContentMap, currentAutomationTriggerThreadId], (threadContentMapValue, threadId) => {
  return getThreadContentValue({
    threadContentMapValue,
    threadId
  });
});

export const currentAutomationTriggerThreadMeta = createSelector([threadMetaById, currentAutomationTriggerThreadId], (threadMetaByIdValue, threadId) => {
  return getThreadMetaValue({
    threadMetaByIdValue,
    threadId
  });
});

export const currentAutomationTriggerRunState = (state) => threadRunState(state, currentAutomationTriggerThreadId(state));

export const currentAutomationTriggerActiveRunId = (state) => threadActiveRunId(state, currentAutomationTriggerThreadId(state));

export const currentAutomationTriggerIsRunning = createSelector([currentAutomationTriggerRunState], (runState) => {
  return !NON_RUNNING_RUN_STATES.has(runState);
});
